// Action bar for grid detail: AI / Clone / Edit / Resume / Stop Grid buttons.
// Back button dropped — browser back covers it and legacy did the same.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GridConfig } from "@/types/api";
import { apiPost, apiDelete } from "@/lib/api-client";
import EditGridModal from "@/components/grid/edit-grid-modal";
import CloneGridModal from "@/components/grid/clone-grid-modal";

interface GridActionsProps {
  grid: GridConfig;
}

// Shared action-button shape so every row button renders at the same height
// regardless of variant. Matches WindActions + legacy .btn.
const BTN_BASE =
  "inline-flex items-center justify-center h-7 px-3 text-xs font-medium rounded border transition-colors disabled:opacity-50";
const BTN_GHOST = `${BTN_BASE} border-border text-dim hover:text-text hover:border-dim`;
const BTN_SUCCESS = `${BTN_BASE} border-success/40 text-success hover:bg-success/10`;
const BTN_DANGER = `${BTN_BASE} border-danger/40 text-danger hover:bg-danger/10`;
const BTN_MUTED = `${BTN_BASE} border-muted/40 text-muted hover:bg-muted/10`;

export default function GridActions({ grid }: GridActionsProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [showClone, setShowClone] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["grid-detail", grid.id] });
    qc.invalidateQueries({ queryKey: ["grids"] });
  };

  const pauseMut = useMutation({
    mutationFn: () => apiPost(`/grids/${grid.id}/pause`),
    onSuccess: invalidate,
  });

  const resumeMut = useMutation({
    mutationFn: () => apiPost(`/grids/${grid.id}/resume`),
    onSuccess: invalidate,
  });

  const closeMut = useMutation({
    // Backend endpoint is /stop (not /close) — cancels all open orders,
    // market-closes any open position, sets status=stopped.
    mutationFn: () => apiPost(`/grids/${grid.id}/stop`),
    onSuccess: () => {
      invalidate();
      navigate("/grids");
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => apiDelete(`/grids/${grid.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grids"] });
      navigate("/grids");
    },
  });

  const isBusy =
    pauseMut.isPending || resumeMut.isPending || closeMut.isPending || deleteMut.isPending;

  return (
    <>
      {/* Renders inline; the detail page wraps this in useDetailActions so
          it lands in the layout's sub-header instead of page content.
          Legacy "AI" button removed — use the AI Advisor card on the
          detail page ("Ask AI" / "Copy Prompt") instead. */}
      <button className={BTN_GHOST} onClick={() => setShowClone(true)}>Clone</button>
      <button className={BTN_GHOST} onClick={() => setShowEdit(true)}>Edit</button>

      {/* Active grid: Pause (soft, keeps orders on book) + Stop (hard,
          cancels orders + closes grid). Paused grid: Resume + Stop. */}
      {grid.status === "active" && (
        <button
          className={BTN_MUTED}
          onClick={() => pauseMut.mutate()}
          disabled={isBusy}
        >
          {pauseMut.isPending ? "Pausing…" : "Pause"}
        </button>
      )}

      {grid.status === "paused" && (
        <button
          className={BTN_SUCCESS}
          onClick={() => resumeMut.mutate()}
          disabled={isBusy}
        >
          {resumeMut.isPending ? "Resuming…" : "Resume"}
        </button>
      )}

      {grid.status !== "stopped" && (
        <button
          className={BTN_DANGER}
          onClick={() => {
            if (confirm(`Stop grid ${grid.symbol}? This will cancel open orders.`)) {
              closeMut.mutate();
            }
          }}
          disabled={isBusy}
        >
          {closeMut.isPending ? "Stopping…" : "Stop"}
        </button>
      )}

      {grid.status === "stopped" && (
        <button
          className={BTN_MUTED}
          onClick={() => {
            if (confirm(`Delete grid ${grid.symbol}? This cannot be undone.`)) {
              deleteMut.mutate();
            }
          }}
          disabled={isBusy}
        >
          {deleteMut.isPending ? "Deleting…" : "Delete"}
        </button>
      )}

      {showEdit && <EditGridModal grid={grid} onClose={() => setShowEdit(false)} />}
      {showClone && <CloneGridModal sourceGrid={grid} onClose={() => setShowClone(false)} />}
    </>
  );
}
